import {
  Box,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Visibility, Videocam } from "@mui/icons-material";
import { AuthedFileLink, EllipsisTooltipText, IssueIndicator } from "../../components-new/common";
import { formatDuration } from "../../../src/utils/timeFormatter.js";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import PrettyDonut from "./PrettyDonut";

import {
  useGetErrorCodesQuery,
  useGetPercentageDataQuery,
  useGetStatusPattern,
  useGetMileStoneTableQuery,
  useTestCaseResultsFromTestSuiteQuery,
} from "../../useQuery";

import { Link } from "react-router-dom";
import { ExeReportHistoryStatus } from "../../components-new/layout/constants";
import ErrorCodeDonut from "./ErrorCodesDonut";
// import TestRunComparisonChart from "./TestRunComparisonChart";

import { useRef, useState, useMemo } from "react";
import { getUserDetails } from "../../utils/userDetailsUtils";
import { PatternBarGraph, PatternDetailsTable } from "./PatternStatusComponents";
import VideoPopup from "../../components/RecordingTable/VideoPopup.jsx";

const StyledTableCell = styled(TableCell)`
  color: #e0e0e0;
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid #e2e6f3;
  background: #2550d1;
`;

const StyledTableBodyCell = styled(TableCell)`
  padding: 8px 20px;
  border-bottom: 1px solid #eee;
  p {
    color: #2b3539;
    font-size: 16px;
    font-weight: 500;
    padding-right: 15px;
    min-width: 60px;
  }
`;

const StyledInfoBox = styled(Box)`
  padding: 16px 20px;
`;

const StyledTypography = styled(Typography)`
  font-size: "14px";
  font-weight: 600;
  color: #232d3d;
`;

const StyledTitle = styled(Typography)`
  color: #e0e0e0;
  font-size: 14px;
  font-weight: 600;
  padding: 16px 20px;
  border-bottom: 2px solid rgba(226, 230, 243, 0.8);
`;

const StyledTitleRight = styled(StyledTitle)`
  flex: 1;
  text-align: center;
`;

const StyledTitleLeft = styled(StyledTitle)`
  flex: 2;
  border-right: 2px solid rgba(226, 230, 243, 0.8);
`;

const StyledInfo = styled(StyledTitle)`
  color: #232d3d;
`;

const StyledInfoLeft = styled(StyledInfo)`
  border-right: 2px solid rgba(226, 230, 243, 0.8);
  flex: 2;
`;

const StyledInfoRight = styled(StyledInfo)`
  flex: 1;
  text-align: center;
  texttransform: "uppercase";
`;

const StyledColumnTitle = styled(StyledTitle)`
  text-align: center;
  padding: 12px 10px;
  flex: 1;
  &:not(:last-child) {
    border-right: 2px solid rgba(226, 230, 243, 0.8);
  }
`;

const StyledColumnValue = styled(StyledInfo)`
  text-align: center;
  padding: 12px 10px;
  flex: 1;
  &:not(:last-child) {
    border-right: 2px solid rgba(226, 230, 243, 0.8);
  }
`;

export default function PrettyReports({ data, testSuiteName, products }) {
  const componentRef = useRef();
  const suitesList = useSelector((state) => state.executionSlice.selectedTestSuiteData);
  const selectedProduct = useSelector((state) => state.executionSlice.selectedProduct);
  const selectedTestSuite = useSelector((state) => state.executionSlice.selectedTestSuite);
  const prodName = products?.find((item) => item.idproducts === selectedProduct);
  const showDetailedSuiteReport = getUserDetails()?.company?.show_detailed_suite_report === 0

  const { data: testCaseData } = useTestCaseResultsFromTestSuiteQuery(
    data.idtest_suite_results,
    "All",
    {
      enabled: true,
      refetchInterval: data.Status === "STARTED" || data.Status === "RUNNING" ? 5000 : false,
    },
  );

  const { data: errorCodesData01 } = useGetErrorCodesQuery(data.idtest_suite_results, {
    enabled: selectedTestSuite !== "",
  });

  const { data: patternData } = useGetStatusPattern(data.idtest_suite_results, {
    enabled: selectedTestSuite !== "",
  });

  const { data: mileStoneData } = useGetMileStoneTableQuery(data.idtest_suite_results, {
    enabled: !showDetailedSuiteReport && selectedTestSuite !== "",
  });

  // const { data: percentageData } = useGetPercentageDataQuery(
  //   data.idtest_suite_results,
  //   selectedTestSuite,
  //   {
  //     enabled: selectedTestSuite !== "",
  //   },
  // );

  // check if any test case has similarity data
  const hasSimilarityData = useMemo(() => {
    return testCaseData?.testcaseresult?.some(
      (item) => item.Similarity !== null && item.Similarity !== undefined && item.Similarity !== "",
    );
  }, [testCaseData?.testcaseresult]);

  const getErrorCodesForDisplay = () => {
    if (!errorCodesData01 || !errorCodesData01.error_codes) {
      return [];
    }

    return Object.entries(errorCodesData01.error_codes).map(([code, details]) => ({
      code,
      message: details.message,
      count: details.failedTestCasesCount,
      firstRunCount: details.firstRunCount || 0,
      rerunCount: details.rerunCount || 0,
    }));
  };

  const errorCodes = getErrorCodesForDisplay();
  const [videoOpen, setVideoOpen] = useState(false);
const [selectedVideoUrl, setSelectedVideoUrl] = useState("");
const [selectedVideoName, setSelectedVideoName] = useState("");

const handleVideoOpen = (url, name) => {
  setSelectedVideoUrl(url);
  setSelectedVideoName(name);
  setVideoOpen(true);
};
const handleVideoClose = () => setVideoOpen(false);
  return (
    <Box>
      <Box ref={componentRef}>
        {showDetailedSuiteReport && (
          <Box
            mt={2}
            display={"flex"}
            bgcolor="#ffffff"
            borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}
            sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}
          >
            <StyledInfoBox flex={1}>
              <EllipsisTooltipText
                text={`Product: ${prodName?.productname}`}
                mode="chars"
                maxChars={50}
              />
            </StyledInfoBox>
            <StyledInfoBox
              flex={1}
              borderRight={`2px solid rgba(226, 230, 243, 0.80)`}
              borderLeft={`2px solid rgba(226, 230, 243, 0.80)`}
            >
              <EllipsisTooltipText text={`Session: `} mode="chars" maxChars={50} />
            </StyledInfoBox>
            <StyledInfoBox flex={1}>
              <EllipsisTooltipText
                text={`Build: ${data.build ? data.build : "#"} `}
                mode="chars"
                maxChars={50}
              />
            </StyledInfoBox>
          </Box>
        )}
        <Box
          mt={4}
          bgcolor={"#2550d1"}
          sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}
        >
          <StyledInfoBox color="#E0E0E0">
            <Typography fontWeight={600}>Execution Information</Typography>
          </StyledInfoBox>
        </Box>
        <Box display="flex" gap={2}>
          <Box
            mt={2}
            flex={1}
            bgcolor={"#fff"}
            sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}
          >
            <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
              <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
                <StyledTypography>Suite Name</StyledTypography>
              </StyledInfoBox>
              <StyledInfoBox flex={2}>
                <StyledTypography>{testSuiteName}</StyledTypography>
              </StyledInfoBox>
            </Box>
            <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
              <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
                <StyledTypography>Session Start Time</StyledTypography>
              </StyledInfoBox>
              <StyledInfoBox flex={2}>
                <StyledTypography>
                  {dayjs(data.Executed_Date).format("DD/MM/YYYY HH:mm:ss")}(IST)
                </StyledTypography>
              </StyledInfoBox>
            </Box>
            {data.noOfDevices > 0 && (
              <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
                <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
                  <StyledTypography>Number of Devices</StyledTypography>
                </StyledInfoBox>
                <StyledInfoBox flex={2}>
                  <StyledTypography>{data.noOfDevices}</StyledTypography>
                </StyledInfoBox>
              </Box>
            )}

            <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
              <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
                <StyledTypography>Execution Time</StyledTypography>
              </StyledInfoBox>
              <StyledInfoBox flex={2}>
                <StyledTypography>{formatDuration(data.Duration)}</StyledTypography>
              </StyledInfoBox>
            </Box>
          </Box>
          <Box
            mt={2}
            flex={1}
            bgcolor={"#fff"}
            sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}
          >
            <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
              <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
                <StyledTypography>No. of Test Cases</StyledTypography>
              </StyledInfoBox>
              <StyledInfoBox flex={2}>
                <StyledTypography>{data.totalCount}</StyledTypography>
              </StyledInfoBox>
            </Box>
            <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
              <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
                <StyledTypography>Self Healed</StyledTypography>
              </StyledInfoBox>
              <StyledInfoBox flex={2}>
                <StyledTypography>{data.selfHealedCount || "N/A"}</StyledTypography>
              </StyledInfoBox>
            </Box>
            <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
              <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
                <StyledTypography>Session End Time</StyledTypography>
              </StyledInfoBox>
              <StyledInfoBox flex={2}>
                <StyledTypography>
                  {dayjs(data.Executed_Date)
                    .add(data.Duration, "second")
                    .format("DD/MM/YYYY HH:mm:ss")}
                  (IST)
                </StyledTypography>
              </StyledInfoBox>
            </Box>
            <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
              <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
                <StyledTypography>Execution Status</StyledTypography>
              </StyledInfoBox>
              <StyledInfoBox flex={2}>
                <IssueIndicator
                  status={data.Status}
                  issues={data.issues}
                  issueType={data.issueType}
                  showStatusText={true}
                />
              </StyledInfoBox>
            </Box>
          </Box>
        </Box>

        {/* {percentageData && <TestRunComparisonChart data={percentageData} />} */}

        {/* Donuts and Bar Graph */}
        <Box display="flex" mt={4} gap={2}>
          {/* First Donut */}
          <Box flex={1}>
            <PrettyDonut data={data} simplified={!showDetailedSuiteReport} />
          </Box>

          {/* Second Donut */}
          {showDetailedSuiteReport && (
            <Box flex={1}>{errorCodesData01 && <ErrorCodeDonut data={errorCodesData01} />}</Box>
          )}

          {/* Bar Graph */}
          {showDetailedSuiteReport && (
            <Box flex={2}>{patternData && <PatternBarGraph patterns={patternData.patterns} />}</Box>
          )}
        </Box>

        {/* Total Test Cases Table */}
        <Box mt={4}>
          <Box
            bgcolor={"#2550D1"}
            sx={{
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              color="#E0E0E0"
              fontWeight={600}
              padding="16px 20px"
              borderBottom={1}
              borderColor={"gray"}
            >
              Test Suite Execution Summary
            </Typography>
          </Box>

          {/* Header row with column titles */}
          <Box display="flex" bgcolor={"#2550D1"}>
            <StyledColumnTitle>Total Test Cases</StyledColumnTitle>
            <StyledColumnTitle sx={{ backgroundColor: "#10B981" }}>Pass</StyledColumnTitle>
            <StyledColumnTitle sx={{ backgroundColor: "#EF4444" }}>Fail</StyledColumnTitle>
            <StyledColumnTitle>Not Executed</StyledColumnTitle>
            <StyledColumnTitle>Skipped Over</StyledColumnTitle>
            <StyledColumnTitle sx={{ backgroundColor: "#10B981" }}>
              First Run Pass
            </StyledColumnTitle>
            <StyledColumnTitle sx={{ backgroundColor: "#EF4444" }}>
              First Run Fail
            </StyledColumnTitle>
            <StyledColumnTitle sx={{ backgroundColor: "#10B981" }}>
              Second Run Pass
            </StyledColumnTitle>
            <StyledColumnTitle sx={{ backgroundColor: "#EF4444" }}>
              Second Run Fail
            </StyledColumnTitle>
          </Box>

          {/* Values row */}
          <Box display="flex" bgcolor={"#ffffff"}>
            <StyledColumnValue>{data.totalCount}</StyledColumnValue>
            <StyledColumnValue>{data.passCount}</StyledColumnValue>
            <StyledColumnValue>{data.failCount}</StyledColumnValue>
            <StyledColumnValue>
              {data.totalCount - (data.passCount + data.failCount)}
            </StyledColumnValue>
            <StyledColumnValue>{data?.skippedOver || "0"}</StyledColumnValue>
            <StyledColumnValue>{data?.firstRunPassCount || "0"}</StyledColumnValue>
            <StyledColumnValue>{data?.firstRunFailCount || "0"}</StyledColumnValue>
            <StyledColumnValue>{data?.secondRunPassCount || "0"}</StyledColumnValue>
            <StyledColumnValue>{data?.secondRunFailCount || "0"}</StyledColumnValue>
          </Box>
        </Box>

        {/* Error Codes Table - With 3 error codes per row and tooltips */}
        {showDetailedSuiteReport && (
          <Box
            flex={1}
            overflow="hidden"
            mt={4}
            sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
          >
            {/* Table Header */}
            <TableContainer component={Box}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#2550D1" }}>
                    <TableCell
                      sx={{
                        color: "#E0E0E0",
                        fontWeight: 600,
                        borderBottom: "1px solid #e2e6f3",
                        width: "25%",
                        borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                      }}
                    >
                      Error Code
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#E0E0E0",
                        fontWeight: 600,
                        borderBottom: "1px solid #e2e6f3",
                        width: "8%",
                        textAlign: "center",
                        borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                      }}
                    >
                      <Tooltip title="First Run / Second Run Count" placement="top" arrow>
                        <span>First/Second</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#E0E0E0",
                        fontWeight: 600,
                        borderBottom: "1px solid #e2e6f3",
                        width: "25%",
                        borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                      }}
                    >
                      Error Code
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#E0E0E0",
                        fontWeight: 600,
                        borderBottom: "1px solid #e2e6f3",
                        width: "8%",
                        textAlign: "center",
                        borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                      }}
                    >
                      <Tooltip title="First Run / Second Run Count" placement="top" arrow>
                        <span>First/Second</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#E0E0E0",
                        fontWeight: 600,
                        borderBottom: "1px solid #e2e6f3",
                        width: "25%",
                        borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                      }}
                    >
                      Error Code
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#E0E0E0",
                        fontWeight: 600,
                        borderBottom: "1px solid #e2e6f3",
                        width: "8%",
                        textAlign: "center",
                      }}
                    >
                      <Tooltip title="First Run / Second Run Count" placement="top" arrow>
                        <span>First/Second</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errorCodes.length > 0 ? (
                    // Create rows with three error codes per row
                    Array.from({ length: Math.ceil(errorCodes.length / 3) }).map((_, i) => (
                      <TableRow key={i} sx={{ backgroundColor: "#ffffff" }}>
                        {/* First error in row */}
                        <TableCell
                          sx={{
                            padding: "16px 20px",
                            borderBottom: "1px solid #eee",
                            borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                          }}
                        >
                          {errorCodes[i * 3] ? (
                            <>
                              <Typography color="#232D3D" fontWeight={600} fontSize="14px">
                                {errorCodes[i * 3].code}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" fontSize="12px">
                                {errorCodes[i * 3].message}
                              </Typography>
                            </>
                          ) : null}
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "16px 20px",
                            borderBottom: "1px solid #eee",
                            textAlign: "center",
                            borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                          }}
                        >
                          {errorCodes[i * 3] ? (
                            <Typography color="#232D3D" fontWeight={600} fontSize="14px">
                              {`${errorCodes[i * 3].firstRunCount}/${errorCodes[i * 3].rerunCount}`}
                            </Typography>
                          ) : null}
                        </TableCell>

                        {/* Second error in row (if exists) */}
                        <TableCell
                          sx={{
                            padding: "16px 20px",
                            borderBottom: "1px solid #eee",
                            borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                          }}
                        >
                          {errorCodes[i * 3 + 1] ? (
                            <>
                              <Typography color="#232D3D" fontWeight={600} fontSize="14px">
                                {errorCodes[i * 3 + 1].code}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" fontSize="12px">
                                {errorCodes[i * 3 + 1].message}
                              </Typography>
                            </>
                          ) : null}
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "16px 20px",
                            borderBottom: "1px solid #eee",
                            textAlign: "center",
                            borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                          }}
                        >
                          {errorCodes[i * 3 + 1] ? (
                            <Typography color="#232D3D" fontWeight={600} fontSize="14px">
                              {`${errorCodes[i * 3 + 1].firstRunCount}/${errorCodes[i * 3 + 1].rerunCount}`}
                            </Typography>
                          ) : null}
                        </TableCell>

                        {/* Third error in row (if exists) */}
                        <TableCell sx={{ padding: "16px 20px", borderBottom: "1px solid #eee" }}>
                          {errorCodes[i * 3 + 2] ? (
                            <>
                              <Typography color="#232D3D" fontWeight={600} fontSize="14px">
                                {errorCodes[i * 3 + 2].code}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" fontSize="12px">
                                {errorCodes[i * 3 + 2].message}
                              </Typography>
                            </>
                          ) : null}
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "16px 20px",
                            borderBottom: "1px solid #eee",
                            textAlign: "center",
                          }}
                        >
                          {errorCodes[i * 3 + 2] ? (
                            <Typography color="#232D3D" fontWeight={600} fontSize="14px">
                              {`${errorCodes[i * 3 + 2].firstRunCount}/${errorCodes[i * 3 + 2].rerunCount}`}
                            </Typography>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow sx={{ backgroundColor: "#ffffff" }}>
                      <TableCell
                        sx={{
                          padding: "16px 20px",
                          borderBottom: "1px solid #eee",
                          borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                        }}
                      >
                        <Typography color="#232D3D" fontWeight={600} fontSize="14px">
                          No error codes available
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "16px 20px",
                          borderBottom: "1px solid #eee",
                          textAlign: "center",
                          borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                        }}
                      >
                        <Typography color="#232D3D" fontWeight={600} fontSize="14px">
                          0/0
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "16px 20px",
                          borderBottom: "1px solid #eee",
                          borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                        }}
                      >
                        No error codes available
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "16px 20px",
                          borderBottom: "1px solid #eee",
                          borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                        }}
                      >
                        0/0
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "16px 20px",
                          borderBottom: "1px solid #eee",
                          borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                        }}
                      >
                        No error codes available
                      </TableCell>
                      <TableCell sx={{ padding: "16px 20px", borderBottom: "1px solid #eee" }}>
                        0/0
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        <Box display="flex" mt={4} gap={2} pb={5}>
          {showDetailedSuiteReport && (
            <Box
              flex={1}
              overflow="hidden"
              sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
            >
              <Box display="flex" bgcolor={"#2550D1"}>
                <StyledTitleLeft>Execution Settings</StyledTitleLeft>
                <StyledTitleRight>Value</StyledTitleRight>
              </Box>
              <Box display="flex" bgcolor={"#ffffff"}>
                <StyledInfoLeft>Browser</StyledInfoLeft>
                <StyledInfoRight>{(suitesList[0]?.browser || "0").toString()}</StyledInfoRight>
              </Box>
              <Box display="flex" bgcolor={"#ffffff"}>
                <StyledInfoLeft>Env</StyledInfoLeft>
                <StyledInfoRight sx={{ overflowWrap: "anywhere" }}>
                  {(suitesList[0]?.envurl ?? "NA").toString()}
                </StyledInfoRight>
              </Box>
              <Box display="flex" bgcolor={"#ffffff"}>
                <StyledInfoLeft>Stop test after fail</StyledInfoLeft>
                <StyledInfoRight>
                  {(suitesList[0]?.stopafterfailure || "NA").toString().toUpperCase()}
                </StyledInfoRight>
              </Box>
              <Box display="flex" bgcolor={"#ffffff"}>
                <StyledInfoLeft>Rerun fail test</StyledInfoLeft>
                <StyledInfoRight>
                  {(suitesList[0]?.rerunfailedtests || "NA").toString().toUpperCase()}
                </StyledInfoRight>
              </Box>
              <Box display="flex" bgcolor={"#ffffff"}>
                <StyledInfoLeft>Run Tests in Parallel</StyledInfoLeft>
                <StyledInfoRight>
                  {(suitesList[0]?.supports_parallel_execution || "NA").toString().toUpperCase()}
                </StyledInfoRight>
              </Box>
              <Box display="flex" bgcolor={"#ffffff"}>
                <StyledInfoLeft>Synchronized Tests</StyledInfoLeft>
                <StyledInfoRight>
                  {(suitesList[0]?.synchedscenarios ?? "").toString().toUpperCase()}
                </StyledInfoRight>
              </Box>
              <Box display="flex" bgcolor={"#ffffff"}>
                <StyledInfoLeft>Headless Execution</StyledInfoLeft>
                <StyledInfoRight>
                  {String(suitesList?.[0]?.headless ?? "NA").toUpperCase()}
                </StyledInfoRight>
              </Box>
              <Box display="flex" bgcolor={"#ffffff"}>
                <StyledInfoLeft>Screenshots on Failure</StyledInfoLeft>
                <StyledInfoRight>
                  {String(suitesList?.[0]?.ssonerror ?? "").toUpperCase()}
                </StyledInfoRight>
              </Box>
              <Box display="flex" bgcolor={"#ffffff"}>
                <StyledInfoLeft>Send Individual Result Email</StyledInfoLeft>
                <StyledInfoRight>
                  {(suitesList[0]?.resultsemail ?? false).toString().toUpperCase()}
                </StyledInfoRight>
              </Box>
            </Box>
          )}
          {/* Pattern Details */}
          {showDetailedSuiteReport && patternData && (
            <PatternDetailsTable patterns={patternData.patterns} />
          )}
        </Box>
      </Box>
      <Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>Run Date</StyledTableCell>
                <StyledTableCell>Run By</StyledTableCell>
                <StyledTableCell>Run Duration</StyledTableCell>
                <StyledTableCell>Log File</StyledTableCell>
                <StyledTableCell>Browser</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                {hasSimilarityData && <StyledTableCell>Similarity</StyledTableCell>}
                <StyledTableCell>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testCaseData?.testcaseresult?.length ? (
                testCaseData?.testcaseresult?.map((item, index) => (
                  <TableRow key={index}>
                    <StyledTableBodyCell>{item.Executed_Date}(IST)</StyledTableBodyCell>
                    <StyledTableBodyCell>{item.Executed_By}</StyledTableBodyCell>
                    <StyledTableBodyCell>{formatDuration(item.Duration)}</StyledTableBodyCell>

                    <StyledTableBodyCell>
                      <AuthedFileLink
                        url={item.logfile}
                        filename={`${item.testcase || "test-case"}.log`}
                        fallback={item.testcase}
                        className="text-blue-500 underline"
                      >
                        {item.testcase}
                      </AuthedFileLink>
                    </StyledTableBodyCell>

                    <StyledTableBodyCell>{item.Browser}</StyledTableBodyCell>

                    <StyledTableBodyCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            display: "inline-block",
                            minWidth: "82px",
                            padding: "8px 10px",
                            borderRadius: "41px",
                            backgroundColor:
                              item.Status === "PASS"
                                ? "#e7fbe4"
                                : item.Status === "FAIL"
                                  ? "#fbe7e7"
                                  : item.Status === "ABEND"
                                    ? "#eef4fc"
                                    : "#D3D3D3",
                            textAlign: "center",
                            textTransform: "capitalize",
                          }}
                        >
                          {ExeReportHistoryStatus[item.Status]}
                        </Box>
                      </Box>
                    </StyledTableBodyCell>
                    {hasSimilarityData && (
                      <StyledTableBodyCell>
                        <Box
                          sx={{
                            display: "inline-block",
                            minWidth: "82px",
                            padding: "8px 10px",
                            borderRadius: "41px",
                            backgroundColor:
                              item.Status === "PASS"
                                ? "#e7fbe4"
                                : item.Status === "FAIL"
                                  ? "#fbe7e7"
                                  : item.Status === "ABEND"
                                    ? "#eef4fc"
                                    : "#D3D3D3",
                            textAlign: "center",
                            textTransform: "capitalize",
                          }}
                        >
                          {item.Similarity ? ExeReportHistoryStatus[item.Similarity] : "N/A"}
                        </Box>
                      </StyledTableBodyCell>
                    )}
                    <StyledTableBodyCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {item.Status !== ExeReportHistoryStatus.ABORTED && (
                          <Tooltip title="View test results" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                let url = `/new/reports?tcrid=${item.idtest_case_results}&tcname=${item.testcase}&tcRundate=${item.Executed_Date}`;
                                window.open(url, "_blank");
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {item.wasVideoRecorded && (
                          <Tooltip title="View Execution Recording" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleVideoOpen(item.videofileurl, item.testcase)}
                            >
                              <Videocam fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </StyledTableBodyCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  {" "}
                  <StyledTableBodyCell align="center" colSpan={7}>
                    No data available
                  </StyledTableBodyCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <VideoPopup
  videoUrl={selectedVideoUrl}
  open={videoOpen}
  onClose={handleVideoClose}
  tcnameForvideo={selectedVideoName}
/>
      </Box>
      {!showDetailedSuiteReport && mileStoneData?.length > 0 && (
        <Box mt={4}>
          <Box bgcolor={"#2550D1"} sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
            <Typography color="#E0E0E0" fontWeight={600} padding="16px 20px">
              Validations by Test Case
            </Typography>
          </Box>
          <TableContainer component={Box}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#2550D1" }}>
                  <StyledTableCell>#</StyledTableCell>
                  <StyledTableCell>Test Case</StyledTableCell>
                  <StyledTableCell>Module Name</StyledTableCell>
                  <StyledTableCell>Total Validations</StyledTableCell>
                  <StyledTableCell>Validations Passed</StyledTableCell>
                  <StyledTableCell>Validations Failed</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mileStoneData.map((item, index) => (
                  <TableRow key={index}>
                    <StyledTableBodyCell>{index + 1}</StyledTableBodyCell>
                    <StyledTableBodyCell>{item["Test Case"]}</StyledTableBodyCell>
                    <StyledTableBodyCell>{item["Module Name"]}</StyledTableBodyCell>
                    <StyledTableBodyCell>{item["Total Validations"]}</StyledTableBodyCell>
                    <StyledTableBodyCell>{item["Validations Passed"]}</StyledTableBodyCell>
                    <StyledTableBodyCell>{item["Validations Failed"]}</StyledTableBodyCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
